import { Button } from '@iq/react-components';
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import JsonEditor from '../../../../../JsonEditor';
import SimpleModal from '../../../../../SimpleModal/SimpleModal';
import { getPermissionsSchema, getPermissionsUiSchema, getRoleSchema } from '../schema';
import { createMemberAndConnection, updateMember } from '../../../../../../bundles/auth';
import { LIMITED_PERMISSION_ENTITIES, ENITIY_ACTION_MAP } from '../../../../../../constants';

const transformErrors = (errors) =>
  errors.map((error) => {
    const customError = { ...error };
    if (customError.property === '.selectedRole') {
      customError.message = 'is required';
    }
    return customError;
  });

const CustomRolesModal = ({ onCloseModal, members, allPermissions, scope, objectId, member }) => {
  const SYSTEM_PERMISSIONS = new Set(['users', 'instructionals', 'systemMessages']);

  const nonSystemPermissions = allPermissions.filter(
    (permission) => !SYSTEM_PERMISSIONS.has(permission)
  );

  const initialFormData = {
    name: member ? member.type : '',
    selectedRole: member ? member.type : '',
  };

  const [roleFormData, setRoleFormData] = useState(initialFormData);
  const [permissionFormData, setPermissionFormData] = useState({});

  const dispatch = useDispatch();

  useEffect(() => {
    const memberPermissions = members.find((m) => m.type === roleFormData.selectedRole);

    const initialMemberPermissions = Object.fromEntries(
      nonSystemPermissions.map((entity) => [entity, [false, false, false, false]])
    );
    const formattedMemberPermissions = memberPermissions?.permissions.reduce(
      (acc, entityPermission) => {
        const permissionMap = {
          Limited: 0,
          Read: 1,
          Write: 2,
          Delete: 3,
        };

        const formattedPermissions = { ...acc };

        const entity = entityPermission.split('/')[0];
        if (SYSTEM_PERMISSIONS.has(entity)) {
          return formattedPermissions;
        }

        const permission = entityPermission.split('/')[1];
        const unlimitedPermission = permission.replace('Limited', '');
        const isLimited = permission.includes('Limited');

        // if our permissionMap doesn't include the unlimited permission we ignore it
        if (!permissionMap[unlimitedPermission]) {
          return formattedPermissions;
        }

        const currentPermissions = formattedPermissions[entity] || [false, false, false, false];

        // Even if a limited permission, we need to include the unlimited permission (display as checked)
        formattedPermissions[entity] = currentPermissions.with(
          permissionMap[unlimitedPermission],
          true
        );

        if (isLimited) {
          formattedPermissions[entity] = formattedPermissions[entity].with(0, true);
        }

        return formattedPermissions;
      },
      initialMemberPermissions
    );
    setPermissionFormData(formattedMemberPermissions);
  }, [roleFormData.selectedRole]);

  const [step, setStep] = useState(0);
  const formReference = useRef();
  const roleSchema = getRoleSchema(members, member);
  const permissionSchema = getPermissionsSchema(
    nonSystemPermissions,
    Object.keys(LIMITED_PERMISSION_ENTITIES)
  );
  const permissionUiSchema = getPermissionsUiSchema(nonSystemPermissions);

  const onRoleFormChange = ({ formData: changes }) => {
    setRoleFormData(changes);
  };

  const onPermissionFormChange = ({ formData: changes }) => {
    setPermissionFormData(changes);
  };

  const handleOnClose = () => {
    setRoleFormData(initialFormData);
    setPermissionFormData({});
    onCloseModal();
  };

  const onSelectedRole = useCallback(
    (event) => {
      const { errors } = formReference.current.validate(roleFormData);
      if (errors.length > 0) {
        formReference.current.onSubmit(event);
      } else {
        setStep(1);
      }
    },
    [formReference.current, roleFormData]
  );

  const onBack = () => {
    setStep(0);
  };

  const customValidate = useCallback(
    (formData, errors) => {
      const reservedNames = members.filter((m) => m.id !== member?.id).map((m) => m.type);

      if (reservedNames.includes(formData.name)) {
        errors.name.addError(`${formData.name} is a reserved name`);
      }
      if (!formData.name) {
        errors.name.addError('is required');
      }
      return errors;
    },
    [members, member]
  );

  const onPermissionSubmit = () => {
    const permissions = Object.entries(permissionFormData).reduce(
      (acc, [entity, entityActions]) => {
        const shouldBeLimited = entityActions[0];
        for (const [index, actionAllowed] of entityActions.entries()) {
          if (index === 0 && actionAllowed) {
            // if limited is true, create relevant limited permission scopes
            for (const permissionScope of LIMITED_PERMISSION_ENTITIES[entity]) {
              acc.push(`${entity}/${permissionScope}Limited`);
            }
          } else if (
            actionAllowed &&
            shouldBeLimited &&
            !LIMITED_PERMISSION_ENTITIES[entity]?.includes(ENITIY_ACTION_MAP[index])
          ) {
            // if other scope is true and limited true, but limited should not effect the scope
            acc.push(`${entity}/${ENITIY_ACTION_MAP[index]}`);
          } else if (actionAllowed && !shouldBeLimited) {
            // if other scope is true without limiting, add both the unlimited scope
            acc.push(`${entity}/${ENITIY_ACTION_MAP[index]}`);
            // if (LIMITED_PERMISSION_ENTITIES[entity]?.includes(ENITIY_ACTION_MAP[i])) {
            //   acc.push(`${entity}/${ENITIY_ACTION_MAP[i]}Limited`);
            // }
          }
        }
        return acc;
      },
      []
    );

    if (member) {
      dispatch(
        updateMember({
          type: roleFormData.name,
          custom: true,
          scope,
          permissions,
          objectId,
          id: member.id,
        })
      );
    } else {
      dispatch(
        createMemberAndConnection({
          type: roleFormData.name,
          custom: true,
          scope,
          permissions,
          objectId,
        })
      );
    }

    handleOnClose();
  };

  const roleForm = useMemo(
    () => (
      <JsonEditor
        formData={roleFormData}
        schema={roleSchema}
        formRef={formReference}
        showButtons={false}
        showNonEditForm={false}
        showEditButton={false}
        onFormChange={onRoleFormChange}
        editorOnly
        customTransformErrors={(errors) => transformErrors(errors)}
        customValidate={customValidate}
      />
    ),
    [roleFormData, step]
  );

  const permissionForm = useMemo(
    () => (
      <React.Fragment>
        <div className="permissions-header">
          <div></div>
          <h4>Limited</h4>
          <h4>Read</h4>
          <h4>Write</h4>
          <h4>Delete</h4>
        </div>
        <JsonEditor
          formData={permissionFormData}
          schema={permissionSchema}
          showButtons={false}
          uiSchema={permissionUiSchema}
          initialEditMode
          editorOnly
          showEditButton={false}
          onFormChange={onPermissionFormChange}
          onFormSubmit={onPermissionSubmit}
          className={'custom-role-form'}
        />
      </React.Fragment>
    ),
    [permissionFormData, step, formReference.current]
  );

  return (
    <SimpleModal
      onClose={handleOnClose}
      overlayCanClose={false}
      className="custom-role-modal"
      title={member ? 'Edit role' : 'Create new role'}
    >
      <div>{step === 0 ? roleForm : permissionForm}</div>
      <div className="custom-role-modal-button-container">
        <Button
          activity="secondary"
          onClick={step === 0 ? handleOnClose : onBack}
        >
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>

        <Button onClick={step === 0 ? (event) => onSelectedRole(event) : onPermissionSubmit}>
          {step === 0 ? 'Next' : 'Save'}
        </Button>
      </div>
    </SimpleModal>
  );
};

export default CustomRolesModal;
